/**
 * Multi-image exterior render upload for Block B (Lagoon View).
 * Three separate views: Northside, Southside, Center. Saves layout to site_config as JSON.
 */
import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, AlertTriangle, LayoutGrid } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { SiteImageUpload } from './SiteImageUpload';
import {
  getExteriorRenderBlockBLayoutRaw,
  setExteriorRenderBlockBConfig,
  clearExteriorRenderBlockBConfig,
  type ExteriorRenderBlockBSection,
  type ExteriorRenderBlockBConfig,
  type BlockBViewId,
} from '@/lib/admin/site-images';

const DEFAULT_SECTION_WIDTH = 640;
const DEFAULT_SECTION_HEIGHT = 960;
const DEFAULT_TOTAL_WIDTH = DEFAULT_SECTION_WIDTH * 3;
const DEFAULT_TOTAL_HEIGHT = DEFAULT_SECTION_HEIGHT;

const SECTION_IDS: BlockBViewId[] = ['southside', 'center', 'northside'];

const VIEW_LABELS: Record<BlockBViewId, string> = {
  southside: 'Southside View',
  center: 'Center View',
  northside: 'Northside View',
};

/** Default section layout. Positions define combined coordinate space for polygon association; must match getPolygonSection() in front-facing-coordinates. */
function defaultSections(): ExteriorRenderBlockBSection[] {
  return [
    { id: 'southside', url: '', position: { x: 0, y: 0 }, dimensions: { width: DEFAULT_SECTION_WIDTH, height: DEFAULT_SECTION_HEIGHT } },
    { id: 'center', url: '', position: { x: DEFAULT_SECTION_WIDTH, y: 0 }, dimensions: { width: DEFAULT_SECTION_WIDTH, height: DEFAULT_SECTION_HEIGHT } },
    { id: 'northside', url: '', position: { x: DEFAULT_SECTION_WIDTH * 2, y: 0 }, dimensions: { width: DEFAULT_SECTION_WIDTH, height: DEFAULT_SECTION_HEIGHT } },
  ];
}

function normalizeSectionId(id: string): BlockBViewId {
  const lower = id?.toLowerCase();
  if (lower === 'left') return 'southside';
  if (lower === 'right') return 'northside';
  if (lower === 'center' || lower === 'northside' || lower === 'southside') return lower as BlockBViewId;
  return 'center';
}

function parseLayout(value: string | null): ExteriorRenderBlockBConfig | null {
  if (!value || value.trim() === '') return null;
  try {
    const parsed = JSON.parse(value) as { type?: string; sections?: (Omit<ExteriorRenderBlockBSection, 'id'> & { id?: string })[]; totalDimensions?: { width: number; height: number } };
    if (parsed?.type === 'multi-image' && Array.isArray(parsed.sections) && parsed.sections.length > 0 && parsed.totalDimensions) {
      const normalized = parsed.sections
        .filter((s) => s?.id != null && s?.position && s?.dimensions)
        .map((s) => ({ ...s, id: normalizeSectionId(s.id!) } as ExteriorRenderBlockBSection));
      const defaults = defaultSections();
      const byId = new Map(normalized.map((s) => [s.id, s]));
      const sections: ExteriorRenderBlockBSection[] = SECTION_IDS.map((id) => byId.get(id) ?? { ...defaults.find((d) => d.id === id)!, url: '' });
      if (sections.length > 0) return { sections, totalDimensions: parsed.totalDimensions };
    }
  } catch {
    // ignore
  }
  return null;
}

export function MultiImageRenderUpload() {
  const [config, setConfig] = useState<ExteriorRenderBlockBConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      const raw = await getExteriorRenderBlockBLayoutRaw();
      const parsed = parseLayout(raw);
      if (parsed) {
        setConfig(parsed);
      } else {
        setConfig({
          sections: defaultSections(),
          totalDimensions: { width: DEFAULT_TOTAL_WIDTH, height: DEFAULT_TOTAL_HEIGHT },
        });
      }
    } catch (error) {
      console.error('Error loading Block B layout:', error);
      toast({ title: 'Error', description: 'Failed to load Lagoon View layout', variant: 'destructive' });
      setConfig({
        sections: defaultSections(),
        totalDimensions: { width: DEFAULT_TOTAL_WIDTH, height: DEFAULT_TOTAL_HEIGHT },
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const updateSectionUrl = useCallback(
    async (sectionId: BlockBViewId, url: string) => {
      if (!config) return;
      const sections = config.sections.map((s) =>
        s.id === sectionId ? { ...s, url } : s
      );
      const newConfig: ExteriorRenderBlockBConfig = { ...config, sections };
      setConfig(newConfig);
      setSaving(true);
      try {
        const success = await setExteriorRenderBlockBConfig(newConfig);
        if (success) {
          toast({ title: 'Success', description: `${VIEW_LABELS[sectionId]} updated` });
        } else {
          throw new Error('Failed to save');
        }
      } catch (error) {
        console.error('Error saving Block B config:', error);
        toast({ title: 'Error', description: 'Failed to save layout', variant: 'destructive' });
      } finally {
        setSaving(false);
      }
    },
    [config, toast]
  );

  const handleClearMultiImage = useCallback(async () => {
    setSaving(true);
    try {
      const success = await clearExteriorRenderBlockBConfig();
      if (success) {
        setConfig({
          sections: defaultSections(),
          totalDimensions: { width: DEFAULT_TOTAL_WIDTH, height: DEFAULT_TOTAL_HEIGHT },
        });
        toast({ title: 'Success', description: 'Reverted to single image mode. Set exterior_render_block_b_url for Lagoon View.' });
      } else {
        throw new Error('Failed to clear');
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to clear multi-image config', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  }, [toast]);

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const sections = config?.sections ?? defaultSections();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <LayoutGrid className="h-5 w-5" />
          Block B (Lagoon View) – Three Separate Views
        </CardTitle>
        <CardDescription>
          Upload three images for Northside, Southside, and Center views of the Lagoon building. Customers switch between these views via tabs on the Site Plan Building View.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <strong>Warning:</strong> Replacing images may affect traced unit coordinates if dimensions or layout change. You can re-trace units in the Front-Facing Tracer after updating.
          </AlertDescription>
        </Alert>

        {/* Preview: 3 separate view boxes */}
        <div className="space-y-2">
          <h4 className="text-sm font-medium">Preview (separate views)</h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {SECTION_IDS.map((id) => {
              const section = sections.find((s) => s.id === id);
              const url = section?.url;
              return (
                <div key={id} className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground">{VIEW_LABELS[id]}</p>
                  <div className="rounded-lg border bg-muted/30 aspect-[2/3] flex items-center justify-center overflow-hidden">
                    {url ? (
                      <img src={url} alt={VIEW_LABELS[id]} className="w-full h-full object-contain" />
                    ) : (
                      <span className="text-xs text-muted-foreground">No image</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Three upload slots */}
        <div className="grid gap-6 sm:grid-cols-1 lg:grid-cols-3">
          {SECTION_IDS.map((id) => {
            const section = sections.find((s) => s.id === id);
            return (
              <div key={id} className="space-y-2 rounded-lg border p-4">
                <h4 className="text-sm font-medium">{VIEW_LABELS[id]}</h4>
                <SiteImageUpload
                  imageType="exterior_render"
                  block="B"
                  imageNameSuffix={id}
                  existingImageUrl={section?.url || undefined}
                  onImageUpdated={(url) => updateSectionUrl(id, url)}
                  label={VIEW_LABELS[id]}
                  description={`Upload image for the ${VIEW_LABELS[id]}.`}
                  maxImages={1}
                />
              </div>
            );
          })}
        </div>

        {saving && (
          <p className="text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Saving…
          </p>
        )}

        <div className="pt-2 border-t">
          <p className="text-xs text-muted-foreground">
            To use a single image for Lagoon View instead, clear this multi-image config and set the single exterior render URL for Block B in the Exterior Render section below.
          </p>
          <button
            type="button"
            onClick={handleClearMultiImage}
            disabled={saving}
            className="mt-2 text-sm text-muted-foreground underline hover:text-foreground disabled:opacity-50"
          >
            Clear multi-image and revert to single image
          </button>
        </div>
      </CardContent>
    </Card>
  );
}
